import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { crmTerritorialAPI } from '../../services/api';
import { Loader2, Sparkles } from 'lucide-react';

const dinero = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('es-MX')}`);

// P3C — Perfil de necesidad y coincidencias DETERMINISTAS (sin IA) con el inventario de la sección.
export const ClienteCoincidencias = ({ cliente, onCerrar, onActualizado }) => {
  const { toast } = useToast();
  const [perfil, setPerfil] = useState({
    presupuesto_min: cliente.necesidad?.presupuesto_min || '',
    presupuesto_max: cliente.necesidad?.presupuesto_max || '',
    zonas: (cliente.necesidad?.zonas || []).join(', '),
    tipos: (cliente.necesidad?.tipos_inmueble || []).join(', '),
  });
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [creando, setCreando] = useState(null);

  const crearOportunidad = async (x) => {
    setCreando(x.propiedad_id);
    try {
      const r = await crmTerritorialAPI.crearOportunidad(cliente.contacto_id,
        { propiedad_id: x.propiedad_id });
      toast({ title: r.creada ? 'Oportunidad creada' : 'Ya existía una oportunidad abierta',
        description: r.creada
          ? 'El asesor responsable recibió un aviso interno en su bitácora.'
          : r.mensaje });
      onActualizado();
    } catch (e) {
      toast({ title: 'No se pudo crear', description: e.message, variant: 'destructive' });
    } finally { setCreando(null); }
  };

  const consultar = () => {
    setCargando(true);
    crmTerritorialAPI.coincidenciasCliente(cliente.contacto_id)
      .then(setDatos)
      .catch((e) => toast({ title: 'Consulta rechazada', description: e.message,
        variant: 'destructive' }))
      .finally(() => setCargando(false));
  };
  useEffect(consultar, [cliente.contacto_id]);

  const guardar = async () => {
    setGuardando(true);
    try {
      await crmTerritorialAPI.actualizarPerfilCliente(cliente.contacto_id, {
        presupuesto_min: perfil.presupuesto_min === '' ? null : Number(perfil.presupuesto_min),
        presupuesto_max: perfil.presupuesto_max === '' ? null : Number(perfil.presupuesto_max),
        zonas: perfil.zonas.split(',').map((z) => z.trim()).filter(Boolean),
        tipos: perfil.tipos.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean),
      });
      toast({ title: 'Perfil de necesidad actualizado' });
      onActualizado();
      consultar();
    } catch (e) {
      toast({ title: 'No se pudo guardar', description: e.message, variant: 'destructive' });
    } finally { setGuardando(false); }
  };

  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent className="max-w-3xl" data-testid="cliente-coincidencias-dialog">
        <DialogHeader>
          <DialogTitle>{cliente.nombre} · necesidad y coincidencias</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <section className="grid sm:grid-cols-2 gap-3">
            <Input type="number" value={perfil.presupuesto_min} placeholder="Presupuesto mínimo"
                   onChange={(e) => setPerfil({ ...perfil, presupuesto_min: e.target.value })}
                   data-testid="cliente-perfil-min" />
            <Input type="number" value={perfil.presupuesto_max} placeholder="Presupuesto máximo"
                   onChange={(e) => setPerfil({ ...perfil, presupuesto_max: e.target.value })}
                   data-testid="cliente-perfil-max" />
            <Input value={perfil.zonas} placeholder="Zonas (separadas por coma)"
                   onChange={(e) => setPerfil({ ...perfil, zonas: e.target.value })}
                   data-testid="cliente-perfil-zonas" />
            <Input value={perfil.tipos} placeholder="Tipos: casa, departamento, terreno…"
                   onChange={(e) => setPerfil({ ...perfil, tipos: e.target.value })}
                   data-testid="cliente-perfil-tipos" />
            <Button onClick={guardar} disabled={guardando} data-testid="cliente-perfil-guardar">
              {guardando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar necesidad
            </Button>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold flex items-center">
              <Sparkles className="h-4 w-4 mr-2 text-gray-400" />
              Inventario compatible de {datos?.seccion || 'la sección'}
              {datos?.listo_para_emparejar && ` · ${datos.coincidencias} coincidencia(s)`}
            </h3>
            {cargando && (
              <p className="text-sm text-gray-500 flex items-center">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculando…
              </p>
            )}
            {!cargando && datos && !datos.listo_para_emparejar && (
              <p className="text-sm text-gray-600" data-testid="cliente-coincidencias-vacio">
                {datos.mensaje}
              </p>
            )}
            {!cargando && datos?.listo_para_emparejar && (
              <ul className="space-y-2" data-testid="cliente-coincidencias-lista">
                {datos.items.length === 0 && (
                  <li className="text-sm text-gray-500">
                    Nada del inventario de esta sección cumple la necesidad registrada.
                  </li>
                )}
                {datos.items.map((x) => (
                  <li key={x.propiedad_id} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium">{x.nombre}</span>
                      <Badge className={x.coincide ? 'bg-emerald-100 text-emerald-800'
                                                   : 'bg-gray-100 text-gray-700'}>
                        {x.puntaje} pts
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {x.tipo} · {x.operacion} · {dinero(x.precio)} · {x.colonia || x.ciudad} ·
                      asesor {x.asesor || '—'}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">{x.razones.join(' · ')}</p>
                    <Button size="sm" variant="outline" className="mt-2"
                            disabled={creando === x.propiedad_id}
                            onClick={() => crearOportunidad(x)}
                            data-testid={`crear-oportunidad-${x.propiedad_id}`}>
                      {creando === x.propiedad_id
                        ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Crear oportunidad
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-500">
              Cálculo determinista por reglas (sin inteligencia artificial). Umbral {datos?.umbral || 55} de 100.
              Sólo se evalúa el inventario de la sección del cliente.
            </p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClienteCoincidencias;
